/**
 * A provider for local settings.
 * These are stored on the client, and are persisted between sessions.
 * */

import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
export interface ILocalSettingsContext {
  localSettings: Record<string, unknown>;
  setLocalSettings: (value: Record<string, unknown>) => void;
}
interface LocalSettingsProviderProps {
  children: React.ReactNode;
}
const LocalSettingsContext = createContext<ILocalSettingsContext>({
  localSettings: {},
  setLocalSettings: () => {
    /* noop */
  }
});
LocalSettingsContext.displayName = 'LocalSettingsContext';
export const LocalSettingsProvider: React.FC<LocalSettingsProviderProps> = ({
  children
}) => {
  const [localSettings, setLocalSettings] = useLocalStorage('local-settings', {}, {
    raw: false,
    deserializer: (e: string) => JSON.parse(window.atob(e)) as Record<string, unknown>,
    serializer: (e: Record<string, unknown>) => window.btoa(JSON.stringify(e))
  });
  const value = useMemo(() => ({
    localSettings: localSettings ?? {},
    setLocalSettings
  }), [localSettings, setLocalSettings]);

  // Always render the Provider. Returning bare `children` until useIsClient()
  // flipped changed the child slot's element type after hydration, remounting
  // the whole app subtree (~1,400 fibers) on every page load. The gate bought
  // no SSR safety: hooks run before conditional returns, so useLocalStorage
  // already ran server-side and returns its initial value there.
  return <LocalSettingsContext.Provider value={value} data-sentry-element="LocalSettingsContext.Provider" data-sentry-component="LocalSettingsProvider" data-sentry-source-file="index.tsx">
      {children}
    </LocalSettingsContext.Provider>;
};
export const useLocalSettings = () => useContext(LocalSettingsContext);